import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import { timestampToDate } from '../../util/dates';
import { hasPermissionToInitiateTransactions, isUserAuthorized } from '../../util/userHelpers';
import {
  NO_ACCESS_PAGE_INITIATE_TRANSACTIONS,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  createSlug,
} from '../../util/urlHelpers';
import api from '../../api';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const amount = (price.amount / 100).toLocaleString('nl-NL'); // e.g. 32.500
    const formattedPrice = `€\u00A0${amount},-`;
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Helper to get formattedPrice and priceTitle with currency symbol.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'EUR')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceDataCurrencySymbol = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const amount = (price.amount / 100).toLocaleString('nl-NL'); // e.g. 32.500
    const formattedPrice = `€\u00A0${amount},-`;
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Converts Money object to number, which is needed for the search schema (for Google etc.)
 *
 * @param {Money} price
 * @returns {Object} Object with price and priceCurrency fields
 */
export const priceForSchemaMaybe = price => {
  try {
    const schemaPrice = convertMoneyToNumber(price);
    return schemaPrice
      ? {
          price: schemaPrice.toFixed(2),
          priceCurrency: price.currency,
        }
      : {};
  } catch (e) {
    return {};
  }
};

/**
 * Get itemCondition for schema.org Offer
 * Maps listing saleClass and condition to Schema.org ItemCondition values
 * Required field for Google Merchant Listing structured data
 *
 * Listings have:
 * - saleClass: 'new' or 'used' (from LISTING_SALE_CLASS)
 * - condition: 'as-new', 'good', 'fair', 'poor', 'damaged' (from LISTING_CONDITION, only for used items)
 *
 * @param {Object} publicData - Listing's publicData object
 * @returns {string} Schema.org ItemCondition URL
 */
export const getItemConditionForSchema = publicData => {
  const saleClass = publicData?.saleClass; // 'new' or 'used'
  const condition = publicData?.condition; // 'as-new', 'good', 'fair', 'poor', 'damaged'

  // If saleClass is 'new', condition is always NewCondition
  if (saleClass === 'new') {
    return 'https://schema.org/NewCondition';
  }

  // For used items, map condition values to Schema.org ItemCondition
  if (saleClass === 'used' || !saleClass) {
    // Map LISTING_CONDITION values to Schema.org ItemCondition
    const conditionMap = {
      'as-new': 'https://schema.org/UsedCondition', // 'as-new' is still used, just in excellent condition
      good: 'https://schema.org/UsedCondition',
      fair: 'https://schema.org/UsedCondition',
      poor: 'https://schema.org/UsedCondition',
      damaged: 'https://schema.org/DamagedCondition', // Schema.org has DamagedCondition
    };

    if (condition && conditionMap[condition.toLowerCase()]) {
      return conditionMap[condition.toLowerCase()];
    }

    // Default to UsedCondition if saleClass is 'used' but no condition specified
    // Also default to UsedCondition if no saleClass (most boats are pre-owned)
    return 'https://schema.org/UsedCondition';
  }

  // Fallback: default to 'used' for boats (most are pre-owned)
  return 'https://schema.org/UsedCondition';
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const rawVariants = image.attributes.variants || {};
      const variants = Object.fromEntries(
        Object.entries(rawVariants)?.map(([key, value]) => [key?.toLowerCase(), value])
      );

      const variant = variants?.[variantName?.toLowerCase()] || null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // signup and return back to listingPage.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else if (!isUserAuthorized(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else if (!hasPermissionToInitiateTransactions(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const parseContactFormPhone = values => {
  const { senderPhoneNumber, senderPhoneNumberDialCode, ...rest } = values;
  const refferenceNumber = `+${senderPhoneNumberDialCode}`;
  const restOfPhoneNumber =
    senderPhoneNumber && `${senderPhoneNumber?.replace(refferenceNumber, '')}`;

  return {
    ...rest,
    ...(restOfPhoneNumber ? { senderPhoneNumber: { refferenceNumber, restOfPhoneNumber } } : {}),
  };
};

export const handleSubmitInquiry = parameters => values => {
  const { params, getListing, onSendInquiry, setInquiryModalOpen, config, isAuthenticated } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const body = parseContactFormPhone(values);

  onSendInquiry(listing, body, config, isAuthenticated)
    .then(txId => {
      setInquiryModalOpen(false);
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

export const handleToggleFavorites = parameters => isFavorite => {
  const { currentUser, routes, location, history, setFavoriteSignal } = parameters;

  // Only allow signed-in users to save favorites
  if (!currentUser) {
    const state = {
      from: `${location.pathname}${location.search}${location.hash}`,
    };

    // Sign up and return back to the listing page.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else {
    const { params, onUpdateFavorites } = parameters;
    const {
      attributes: { profile },
    } = currentUser;
    const { favorites = [] } = profile.privateData || {};

    let payload;

    if (!profile.privateData || !profile.privateData?.favorites) {
      payload = {
        privateData: {
          favorites: [params.id],
        },
      };
      setFavoriteSignal(true);
      api.listings
        .incrementListingStatistics(params.id, { listingStatisticsType: 'favoritesCount' })
        .catch(() => {});
    } else if (isFavorite) {
      payload = {
        privateData: {
          favorites: favorites.filter(f => f !== params.id),
        },
      };
      setFavoriteSignal(false);
    } else {
      payload = {
        privateData: {
          favorites: [...favorites, params.id],
        },
      };
      setFavoriteSignal(true);
      api.listings
        .incrementListingStatistics(params.id, { listingStatisticsType: 'favoritesCount' })
        .catch(() => {});
    }
    onUpdateFavorites(payload);
  }
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const {
    bookingDates,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    quantity: quantityRaw,
    seats: seatsRaw,
    deliveryMethod,
    ...otherOrderData
  } = values;

  const bookingMaybe = bookingDates
    ? {
        bookingDates: {
          bookingStart: bookingDates.startDate,
          bookingEnd: bookingDates.endDate,
        },
      }
    : bookingStartTime && bookingEndTime
    ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
    : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
  const seats = Number.parseInt(seatsRaw, 10);
  const seatsMaybe = Number.isInteger(seats) ? { seats } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  const initialValues = {
    listing,
    orderData: {
      ...bookingMaybe,
      ...quantityMaybe,
      ...seatsMaybe,
      ...deliveryMethodMaybe,
      ...otherOrderData,
    },
    confirmPaymentError: null,
  };

  const saveToSessionStorage = !currentUser;

  // Customize checkout page state with current listing and selected orderData
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

  // Clear previous Stripe errors from store if there is any
  onInitializeCardPaymentData();

  // Redirect to CheckoutPage
  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, invalidListing, staleListing, closedListing, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        {invalidListing ? (
          <FormattedMessage id="ListingPage.errorInvalidListingMessage" />
        ) : staleListing ? (
          <FormattedMessage id="ListingPage.errorStaleListingMessage" />
        ) : closedListing ? (
          <FormattedMessage id="ListingPage.errorClosedListingMessage" />
        ) : (
          <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
        )}
      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingListingMessage" />
      </p>
    </PlainPage>
  );
};
